import { Component, ErrorInfo, ReactNode } from 'react';
import { errorReporter } from '../utils/errorReporter';

/**
 * Error boundary props
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Error boundary state
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 *
 * Best practices:
 * - Wrap top-level components to prevent full app crashes
 * - Display user-friendly error messages
 * - Log errors for debugging
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error('[ErrorBoundary]: Caught an error', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Report error to monitoring service
    errorReporter.reportComponentError(error, errorInfo, {
      component: 'ErrorBoundary',
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  override render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI (using plain HTML for better compatibility)
      return (
        <div style={{ maxWidth: '800px', margin: '64px auto', padding: '0 16px' }}>
          <div
            style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '24px',
            }}
            role="alert"
          >
            <h2 style={{ margin: '0 0 8px 0', fontSize: '1.5rem' }}>Something went wrong</h2>
            <p style={{ margin: '0 0 16px 0' }}>
              The application encountered an unexpected error. Please try refreshing the page.
            </p>
          </div>

          {error && process.env.NODE_ENV === 'development' && (
            <div style={{ marginBottom: '24px' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>
                Error details (development only):
              </strong>
              <pre
                style={{
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  border: '1px solid #ddd',
                }}
              >
                {error.message}
              </pre>
            </div>
          )}

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: 'white',
                color: '#1976d2',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}
